import { add, toDate } from 'date-fns';
import React, { useContext, useMemo } from 'react';
import { PACKAGES_PATH, studentsPath, userProfilePath } from '../../../functions/src/admin/paths';
import { getSchoolYearPackageCatalogStartAt, toDateFromServer } from '../../../functions/src/api/dates';
import { MealPackage, OrderPerson, Student, UserMealPackage, UserProfile } from '../../../functions/src/types';
import { auth, currentSchoolKey, DataSnapshot } from '../../api/firebase';
import { useDatabase, useDatabaseVal, useList } from '../../api/firebaseHooks';
import { getLangValue, langContext, localeFromLang } from '../../api/Lang';
import { useHasMultipleExchangeableMeals } from '../../api/mealUtils';
import { formatPriceEuro } from '../../api/priceFormat';
import Popup from '../../api/Popup';
import { usePackageMenuPdfUrl } from '../../admin/adminPackages/packageMenuPdfUtils';
import { NUM_DAYS_SUBSCRIPTION_WARNING } from '../../../functions/src/api/constants';
import { useStrings } from '../../common-strings';
import { PACKAGE_GENERAL_INFO_DEFAULTS } from './PackagePopup';
import { usePackagesStrings } from './packages-strings';
import './packages.css';

type PackageSubscriptionDetailPopupProps = {
    userPackageSnap: DataSnapshot<UserMealPackage>;
    onClose: () => void;
};

export default function PackageSubscriptionDetailPopup({ userPackageSnap, onClose }: PackageSubscriptionDetailPopupProps) {
    const [lang] = useContext(langContext);
    const commonStrings = useStrings();
    const packagesStrings = usePackagesStrings();
    const { uid } = auth.currentUser;
    const hasMultipleExchangeableMeals = useHasMultipleExchangeableMeals();
    const [profile] = useDatabaseVal<UserProfile>(userProfilePath(uid));
    const students = useDatabase(studentsPath(uid));

    const paidPackage = userPackageSnap.val();
    const recipientData = students?.child(paidPackage.recipient).val() as OrderPerson | undefined;
    const recipientIsStaff = !!recipientData?.personnel;
    const student: Student | undefined = recipientIsStaff
        ? { firstName: profile?.firstName, lastName: profile?.lastName, classroom: '' }
        : (recipientData as Student | undefined);

    const recipientName = [student?.firstName, student?.lastName].filter(Boolean).join(' ') || '—';

    const mealPackages = useDatabase(PACKAGES_PATH, (ref) => ref.orderByChild('start').startAt(getSchoolYearPackageCatalogStartAt())) || null;
    const mealPackageList = useList(mealPackages);

    const mealPackage: MealPackage | null = useMemo(() => {
        const found = mealPackageList.find(
            (p) => p.child('start').val() === paidPackage.start && p.child('end').val() === paidPackage.end
        );
        return found ? (found.val() as MealPackage) : null;
    }, [mealPackageList, paidPackage.start, paidPackage.end]);

    const packageGeneralInfo = mealPackage?.generalInfo;
    const defaultBullets = [
        getLangValue(PACKAGE_GENERAL_INFO_DEFAULTS[0], lang),
        hasMultipleExchangeableMeals ? getLangValue(PACKAGE_GENERAL_INFO_DEFAULTS[1], lang) : '',
        '',
    ];
    const hasAnyCustomBullet = [0, 1, 2].some((idx) => {
        const customBullet = packageGeneralInfo?.[idx]?.[lang];
        return typeof customBullet === 'string' && !!customBullet.trim();
    });
    const bulletsToShow = [0, 1, 2]
        .map((idx) => {
            const customBullet = packageGeneralInfo?.[idx]?.[lang];
            const trimmedCustom = typeof customBullet === 'string' ? customBullet.trim() : '';
            return hasAnyCustomBullet ? trimmedCustom || defaultBullets[idx] : defaultBullets[idx];
        })
        .filter((bullet) => !!bullet);

    const menuPdfDisplayName = mealPackage?.menuPdfName?.[lang];
    const { url: menuPdfUrl, loading: menuPdfLoading } = usePackageMenuPdfUrl(lang, currentSchoolKey, menuPdfDisplayName);

    const now = toDate(Date.now());
    const endDate = paidPackage.end ? toDateFromServer(paidPackage.end) : null;
    const endMs = endDate ? endDate.getTime() : 0;
    const isActive = !!paidPackage.end && endMs >= Date.now();
    const isEndingSoon =
        isActive &&
        !!endDate &&
        now > add(endDate, { days: -NUM_DAYS_SUBSCRIPTION_WARNING });

    const validityStr = `${paidPackage.start ? toDateFromServer(paidPackage.start).toLocaleDateString(localeFromLang(lang)) : '—'} - ${paidPackage.end ? toDateFromServer(paidPackage.end).toLocaleDateString(localeFromLang(lang)) : '—'}`;
    const priceStr = typeof paidPackage.price === 'number' ? formatPriceEuro(paidPackage.price, lang) : '—';
    const hasPurchaseDate = typeof paidPackage.purchasedAt === 'number';
    const purchaseDateStr = hasPurchaseDate ? new Date(paidPackage.purchasedAt).toLocaleDateString(localeFromLang(lang)) : '';

    return (
        <Popup onClose={onClose}>
            <div className="package-subscription-detail-wrap">
                <div className="package-subscription-detail-inner">
                    <div className="package-subscription-detail-body">
                        <div className="package-subscription-detail-name">{recipientName}</div>
                        <div className="package-subscription-detail-section">
                            <div className="package-subscription-detail-package-title">{paidPackage.title?.[lang]}</div>
                            <ul className="package-subscription-detail-bullets">
                                {bulletsToShow.map((bullet, idx) => (
                                    <li key={`bullet-${idx}`}>{bullet}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="package-subscription-detail-section">
                            <div className="package-subscription-detail-row package-subscription-detail-row-status">
                                <span className="package-subscription-detail-label">{packagesStrings.statusLabel}</span>
                                <span className="package-subscription-detail-value">
                                    <span
                                        className={
                                            !isActive
                                                ? 'package-subscription-detail-status package-subscription-detail-status-inactive'
                                                : isEndingSoon
                                                    ? 'package-subscription-detail-status package-subscription-detail-status-ending'
                                                    : 'package-subscription-detail-status package-subscription-detail-status-active'
                                        }
                                    >
                                        {!isActive ? (
                                            commonStrings.expired
                                        ) : isEndingSoon ? (
                                            <>
                                                <img src="/img-assets/exclamation.svg" alt="" className="package-subscription-detail-ending-icon" />
                                                {commonStrings.endingSoon}
                                            </>
                                        ) : (
                                            <>
                                                <img src="/img-assets/check2.svg" alt="" className="package-subscription-detail-check" />
                                                {commonStrings.active}
                                            </>
                                        )}
                                    </span>
                                </span>
                            </div>
                            {hasPurchaseDate && (
                                <div className="package-subscription-detail-row">
                                    <span className="package-subscription-detail-label">{packagesStrings.purchaseDateLabel}</span>
                                    <span className="package-subscription-detail-value">{purchaseDateStr}</span>
                                </div>
                            )}
                            <div className="package-subscription-detail-row">
                                <span className="package-subscription-detail-label">{packagesStrings.packageValidityLabel}</span>
                                <span className="package-subscription-detail-value">{validityStr}</span>
                            </div>
                            <div className="package-subscription-detail-row">
                                <span className="package-subscription-detail-label">{packagesStrings.packagePriceLabel}</span>
                                <span className="package-subscription-detail-value">{priceStr}</span>
                            </div>
                        </div>
                        <div className="package-subscription-detail-terms">
                            <span className="package-subscription-detail-terms-prefix">{packagesStrings.seeThePrefix} </span>
                            <a
                                href="/terms/terms-packages.html"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="package-subscription-detail-terms-link"
                            >
                                {packagesStrings.termsLabel}
                            </a>
                        </div>
                        {!menuPdfLoading && menuPdfUrl && menuPdfDisplayName && (
                            <div className="package-subscription-detail-pdf">
                                <img src="/img-assets/pdf2.svg" alt="" className="package-subscription-detail-pdf-icon" />
                                <a href={menuPdfUrl} download={menuPdfDisplayName} className="package-subscription-detail-pdf-link">
                                    {commonStrings.downloadInfo}
                                </a>
                            </div>
                        )}
                    </div>
                    <button type="button" className="package-subscription-detail-ok" onClick={onClose}>
                        {commonStrings.ok}
                    </button>
                </div>
            </div>
        </Popup>
    );
}
